export enum LinkedinAdAccountStatus {
  ACTIVE = "ACTIVE",
  CANCELED = "CANCELED",
  DRAFT = "DRAFT",
  PENDING_DELETION = "PENDING_DELETION",
  REMOVED = "REMOVED",
}

export enum LinkedinCampaignStatus {
  ACTIVE = "ACTIVE",
  PAUSED = "PAUSED",
  ARCHIVED = "ARCHIVED",
  COMPLETED = "COMPLETED",
  CANCELED = "CANCELED",
  DRAFT = "DRAFT",
  PENDING_DELETION = "PENDING_DELETION",
  REMOVED = "REMOVED",
}

export enum LinkedinCampaignObjective {
  BRAND_AWARENESS = "BRAND_AWARENESS",
  ENGAGEMENT = "ENGAGEMENT",
  JOB_APPLICANTS = "JOB_APPLICANTS",
  LEAD_GENERATION = "LEAD_GENERATION",
  WEBSITE_CONVERSIONS = "WEBSITE_CONVERSION",
  WEBSITE_VISITS = "WEBSITE_VISIT",
  VIDEO_VIEWS = "VIDEO_VIEW",
}

export enum LinkedinOptimizationTargetType {
  NONE = "NONE",
  ENHANCED_CONVERSION = "ENHANCED_CONVERSION",
  MAX_IMPRESSION = "MAX_IMPRESSION",
  MAX_CLICK = "MAX_CLICK",
  MAX_CONVERSION = "MAX_CONVERSION",
  MAX_VIDEO_VIEW = "MAX_VIDEO_VIEW",
  MAX_LEAD = "MAX_LEAD",
  MAX_REACH = "MAX_REACH",
  TARGET_COST_PER_CLICK = "TARGET_COST_PER_CLICK",
  TARGET_COST_PER_IMPRESSION = "TARGET_COST_PER_IMPRESSION",
  TARGET_COST_PER_VIDEO_VIEW = "TARGET_COST_PER_VIDEO_VIEW",
  CAP_COST_AND_MAXIMIZE_CLICKS = "CAP_COST_AND_MAXIMIZE_CLICKS",
  CAP_COST_AND_MAXIMIZE_IMPRESSIONS = "CAP_COST_AND_MAXIMIZE_IMPRESSIONS",
  CAP_COST_AND_MAXIMIZE_VIDEO_VIEWS = "CAP_COST_AND_MAXIMIZE_VIDEO_VIEWS",
  CAP_COST_AND_MAXIMIZE_LEADS = "CAP_COST_AND_MAXIMIZE_LEADS",
}

export enum LinkedinCostType {
  CPM = "CPM",
  CPC = "CPC",
  CPV = "CPV",
}

export interface LinkedinAdAccount {
  id: number;
  name: string;
  status: LinkedinAdAccountStatus;
}

export interface LinkedinCampaign {
  id: number;
  name: string;
  "test?": boolean;
  status: LinkedinCampaignStatus;
  agent: null | LinkedinAgent;
  objective: LinkedinCampaignObjective;
  "target-type": LinkedinOptimizationTargetType;
  "cost-type": LinkedinCostType;
  "daily-budget": LinkedinMoney | null;
  "unit-cost": LinkedinMoney;
  stats?: LinkedinCampaignStats | null;
  "last-change"?: LinkedinChange | null;
  "offsite-delivery-enabled?": boolean;
  "audience-expansion-enabled?": boolean;
  "targeting-criteria": unknown;
  "campaign-group-urn": string | null;
}

export type LinkedinChangeKind = "bid" | "budget";

// "unknown" covers budget changes recorded before the agent started
// attributing them, where the two are indistinguishable after the fact.
export type LinkedinChangeSource = "agent" | "external" | "unknown";

// A bid or daily budget change, as the activity feeds show it.
export interface LinkedinChange {
  id: string;
  kind: LinkedinChangeKind;
  "campaign-id": number;
  "currency-code": string;
  before: number;
  after: number;
  // Budget rows only -- bid decisions are always the agent's.
  source: LinkedinChangeSource | null;
  "created-at": string;
}

export interface LinkedinCampaignStatsEntry {
  "start-date": string;
  "end-date": string;
  cost: number;
  "cost-local": number;
  "budget-local": number | null;
  clicks: number;
  impressions: number;
  "video-views": number;
}

export interface LinkedinCampaignStats {
  pre: LinkedinCampaignStatsEntry[];
  post: LinkedinCampaignStatsEntry[];
}

export interface LinkedinMoney {
  amount: number;
  "currency-code": string;
}

export interface LinkedinAudienceCount {
  active: number;
  total: number;
}

export enum LinkedinAgentMode {
  AUTO = "auto",
  MANUAL = "manual",
}

export enum LinkedinAgentStatus {
  ACTIVE = "active",
  PAUSED = "paused",
}

export enum LinkedinAgentPauseReason {
  CAMPAIGN_INACTIVE = "campaign-inactive",
  CAMPAIGN_WITHOUT_BUDGET = "campaign-without-budget",
  NO_RECENT_ACTIVITY = "no-recent-activity",
  PAUSED_BY_USER = "paused-by-user",
}

export interface LinkedinAgent {
  id: number;
  "workspace-id": number;
  "ad-account-id": number;
  "campaign-id": number;
  "latest-budget-currency-code": string;
  "latest-budget-amount": number;
  mode: LinkedinAgentMode;
  status: LinkedinAgentStatus;
  "pause-reason": null | LinkedinAgentPauseReason;
  "mode-toggle-deadline": string;
  timezone: string | null;
  "last-started-at": string;
  "last-paused-at": string;
  "baseline-last-activated-at": string | null;
  "baseline-last-deactivated-at": string | null;
  "weekly-budget-currency-code": string | null;
  "weekly-budget-amount": number | null;
  "weekly-budget-max-daily-amount": number | null;
  "created-at": string;
  "updated-at": string;
}
